import React, { useState, useRef, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setButtonsCombine } from "../../../store/slices/athleteSlice";

const SCORE_OPTIONS = {
  TRACKIT: "Trackit Score",
  POSITION: "Position Score",
  POSITION_YEAR: "Position/Age Score",
};

const OPTIONS = [
  SCORE_OPTIONS.TRACKIT,
  SCORE_OPTIONS.POSITION,
  SCORE_OPTIONS.POSITION_YEAR,
];

const getButtonsCombinePayload = (option) => ({
  athlete: option === SCORE_OPTIONS.TRACKIT,
  position: option === SCORE_OPTIONS.POSITION,
  positionYear: option === SCORE_OPTIONS.POSITION_YEAR,
});

const DEFAULT_SCORE_OPTION = SCORE_OPTIONS.POSITION;
const clampPercentage = (value) => Math.min(Math.max(value, 0), 100);
const ANIMATION_DURATION = 1200;

export default function AthleteCombineButtons({ profile = {} }) {
  const [showModal, setShowModal] = useState({
    trackit: false,
    position: false,
    positionYear: false,
  });

  const [isOpen, setIsOpen] = useState(false);
  const [selected, setSelected] = useState(SCORE_OPTIONS.POSITION);
  const dropdownRef = useRef(null);
  const hasManualSelection = useRef(false);

  const r_athlete = useSelector((state) => state.athletes);

  const dispatch = useDispatch();

  const applySelection = useCallback(
    (option, { markManual = true } = {}) => {
      if (markManual) hasManualSelection.current = true;
      setSelected(option);
      dispatch(setButtonsCombine(getButtonsCombinePayload(option)));
    },
    [dispatch],
  );

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (hasManualSelection.current) return;
    const defaultOption = DEFAULT_SCORE_OPTION;
    applySelection(defaultOption, { markManual: false });
  }, [applySelection]);

  const selectedScore =
    selected === SCORE_OPTIONS.TRACKIT
      ? (profile.trackittScore ?? 0)
      : selected === SCORE_OPTIONS.POSITION
        ? (profile.positionScore ?? 0)
        : (profile.positionYearScore ?? profile.positionScore ?? 0);
  const positionYearRank = profile?.positionYearRank;
  const hasPositionYearRank =
    positionYearRank != null && positionYearRank !== "";
  const safeSelectedScore = Number(selectedScore) || 0;
  const [animatedScoreWidth, setAnimatedScoreWidth] = useState(0);
  const [scoreValue, setScoreValue] = useState(0);

  useEffect(() => {
    const frame = requestAnimationFrame(() => {
      setAnimatedScoreWidth(clampPercentage(safeSelectedScore));
    });

    return () => cancelAnimationFrame(frame);
  }, [safeSelectedScore]);

  useEffect(() => {
    let frame;
    let startTime;

    const animateScore = (time) => {
      if (!startTime) startTime = time;
      const elapsed = time - startTime;
      const progress = Math.min(elapsed / ANIMATION_DURATION, 1);
      const nextScore = safeSelectedScore * progress;
      setScoreValue(progress === 1 ? safeSelectedScore : Math.round(nextScore));

      if (progress < 1) {
        frame = requestAnimationFrame(animateScore);
      }
    };

    frame = requestAnimationFrame(animateScore);
    return () => cancelAnimationFrame(frame);
  }, [safeSelectedScore]);

  return (
    <>
      <div className=" flex items-stretch gap-4 py-4">
        <div className="w-full font-medium flex items-center">
          {r_athlete.config.showTrackittScore && (
            <div className="w-full flex flex-col md:flex-row items-stretch gap-4">
              <div
                className="w-full xl:w-48 lg:w-48 
             text-gray-800 bg-white 
             rounded-md border border-blue-300 
             py-2 px-3 font-medium"
              >
                <div className="flex items-center justify-start gap-1 pb-2">
                  <span className="text-sm font-medium text-gray-800">
                    {selected}
                  </span>
                  <div className="ml-2 flex items-center justify-start gap-1">
                    <span
                      className="cursor-pointer text-sm text-gray-500"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setShowModal({
                          trackit: selected === SCORE_OPTIONS.TRACKIT,
                          position: selected === SCORE_OPTIONS.POSITION,
                          positionYear:
                            selected === SCORE_OPTIONS.POSITION_YEAR,
                        });
                      }}
                    >
                      <svg
                        width="18px"
                        height="18px"
                        viewBox="-2 -2 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                        preserveAspectRatio="xMinYMin"
                        className="jam jam-info info-icon"
                      >
                        <path d="M10 20C4.477 20 0 15.523 0 10S4.477 0 10 0s10 4.477 10 10-4.477 10-10 10zm0-2a8 8 0 1 0 0-16 8 8 0 0 0 0 16zm0-10a1 1 0 0 1 1 1v5a1 1 0 0 1-2 0V9a1 1 0 0 1 1-1zm0-1a1 1 0 1 1 0-2 1 1 0 0 1 0 2z" />
                      </svg>{" "}
                    </span>
                  </div>
                </div>

                <div className="flex justify-between">
                  <div className="w-full bg-gray-300 rounded-full h-1 mt-2.5 mr-4">
                    <div
                      className="bg-[#3BCE4F] h-1 rounded-full"
                      style={{
                        width: `${animatedScoreWidth}%`,
                        transition: `width ${ANIMATION_DURATION}ms ease-out`,
                      }}
                    ></div>
                  </div>
                  <div className="text-sm font-bold text-gray-800">
                    {scoreValue}
                  </div>
                </div>
              </div>

              {selected === SCORE_OPTIONS.POSITION_YEAR && (
                <div className="w-full lg:w-56 text-gray-800 bg-white rounded-md border border-blue-300 py-2 px-3 font-medium">
                  <div className="text-xl font-bold leading-none text-gray-900 pb-2">
                    {hasPositionYearRank ? (
                      <>
                        <span className="font-normal text-gray-500 pr-1">
                          #
                        </span>
                        {positionYearRank}
                      </>
                    ) : (
                      "--"
                    )}
                  </div>
                  <div className="text-sm text-gray-600 whitespace-nowrap">
                    Position rank within your class
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
        <div className="w-full sm:w-auto sm:ml-auto flex place-items-start justify-end">
          <div className="relative inline-block text-left" ref={dropdownRef}>
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="flex items-center justify-end gap-2 px-3 py-2 text-sm text-nowrap
                   bg-white border border-gray-300 rounded-md
                   shadow-sm hover:shadow-md transition-all duration-200
                   "
            >
              <div className="flex items-center gap-0">
                <svg
                  width="20px"
                  height="20px"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M4 7a1 1 0 0 1 1-1h14a1 1 0 1 1 0 2H5a1 1 0 0 1-1-1zm2 5a1 1 0 0 1 1-1h10a1 1 0 1 1 0 2H7a1 1 0 0 1-1-1zm2 5a1 1 0 0 1 1-1h6a1 1 0 1 1 0 2H9a1 1 0 0 1-1-1z"
                    fill="#0D0D0D"
                  />
                </svg>
                <span className="text-gray-800 font-medium">{selected}</span>
              </div>
            </button>

            {/* Dropdown Menu */}
            {isOpen && (
              <div
                className="absolute mt-1 w-full bg-white border border-gray-200 
                     rounded-md shadow-lg overflow-hidden z-50"
              >
                {OPTIONS.map((option, index) => (
                  <div key={option}>
                    <button
                      onClick={() => {
                        applySelection(option);
                        setIsOpen(false);
                      }}
                      className={`w-full text-left px-3 py-2 text-gray-700 text-sm
                           hover:bg-gray-100 transition-colors duration-150
                           ${
                             selected === option ? "bg-gray-50 font-medium" : ""
                           }`}
                    >
                      {option}
                    </button>

                    {index === 0 && (
                      <div className="border-t border-gray-200" />
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {showModal.trackit && (
        <Modal
          title="Trackit Score Info"
          description="This score comes from comparing you to every other player that is within your (their) weight range (+ or - 20 lbs) and age regardless of position. Your overall track it score is a combination of your 'track it athletic score' and your 'track it measurement score'. We only compare results that have been verified through one of our combines."
          onClose={() =>
            setShowModal({
              trackit: false,
              position: false,
              positionYear: false,
            })
          }
        />
      )}

      {showModal.position && (
        <Modal
          title="Position Score Info"
          description="This score comes from comparing you to every other player that is in your position group. Age and weight do not play a role in determining this score. Your overall position score is a combination of your 'position athletic score' and your 'position measurement score'. We only compare results that have been verified through one of our combines"
          onClose={() =>
            setShowModal({
              trackit: false,
              position: false,
              positionYear: false,
            })
          }
        />
      )}
      {showModal.positionYear && (
        <Modal
          title="Position/Age Score Info"
          description="This score comes from comparing you to every other player that is in your position group and is your age or younger at the time of the combine.  Your overall position/age score is a combination of your 'position/age athletic score' and your 'position/age measurement score'. We only compare results that have been verified through one of our combines."
          onClose={() =>
            setShowModal({
              trackit: false,
              position: false,
              positionYear: false,
            })
          }
        />
      )}
    </>
  );
}

function Modal({ title, description, onClose }) {
  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
      <div className="bg-white rounded-lg p-6 w-3/4 max-w-md">
        <h2 className="text-lg font-bold mb-4">{title}</h2>
        <p className="text-sm mb-6">{description}</p>
        <button
          onClick={onClose}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
        >
          Close
        </button>
      </div>
    </div>
  );
}
